import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { api } from '../api/client';

interface SettingsContextType {
  logoUrl: string | null;
  accentColor: string | null;
  setAccentColor: (hex: string | null) => Promise<void>;
  refresh: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const ACCENT_STYLE_ID = 'evolution-accent-override';

/**
 * Admin-chosen accent: one hex drives the whole accent family in both themes
 * via an injected stylesheet (light uses the hex as-is, dark gets a brightened
 * variant so it carries on dark surfaces; dim/mid mix against the live
 * surface token, so they follow the theme automatically).
 */
function applyAccent(hex: string | null) {
  const existing = document.getElementById(ACCENT_STYLE_ID);
  if (existing) existing.remove();
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return;
  const style = document.createElement('style');
  style.id = ACCENT_STYLE_ID;
  style.textContent = `
:root {
  --accent: ${hex};
  --accent-hover: color-mix(in srgb, ${hex} 82%, #000);
  --accent-dim: color-mix(in srgb, ${hex} 13%, var(--surface));
  --accent-mid: color-mix(in srgb, ${hex} 45%, var(--surface));
}
[data-theme="dark"] {
  --accent: color-mix(in srgb, ${hex} 62%, #ffffff);
  --accent-hover: color-mix(in srgb, ${hex} 48%, #ffffff);
  --accent-dim: color-mix(in srgb, ${hex} 20%, var(--surface));
  --accent-mid: color-mix(in srgb, ${hex} 40%, var(--surface));
}`;
  document.head.appendChild(style);
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [accentColor, setAccentColorState] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const settings = await api.get<Record<string, string>>('/api/admin/settings');
      if (settings.logo_uploaded === 'true') {
        // Cache-bust each fetch so a freshly uploaded logo is shown immediately.
        setLogoUrl(`/uploads/logo.png?t=${Date.now()}`);
      } else {
        setLogoUrl(null);
      }
      const accent = settings.accent_color && /^#[0-9a-fA-F]{6}$/.test(settings.accent_color)
        ? settings.accent_color
        : null;
      setAccentColorState(accent);
      applyAccent(accent);
    } catch {
      setLogoUrl(null);
    }
  }, []);

  const setAccentColor = useCallback(async (hex: string | null) => {
    await api.put('/api/admin/settings/accent_color', { value: hex ?? '' });
    setAccentColorState(hex);
    applyAccent(hex);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SettingsContext.Provider value={{ logoUrl, accentColor, setAccentColor, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
